// Playful "thinking" labels for AI loading states.
// Two flavors: short whimsical -ing verbs (Claude-style) and longer
// cannabis-industry action sentences. AIThinkingLabel mixes both.

export type QuipContext =
  | 'search'
  | 'chat'
  | 'brief'
  | 'description'
  | 'classifier'
  | 'generic';

// ~60 short -ing verbs. Keep tone playful, professional-safe, no
// consumption references. Each renders as `Word…`.
export const SHORT_VERBS: string[] = [
  'Ruminating', 'Pondering', 'Rummaging', 'Marinating', 'Percolating',
  'Simmering', 'Sleuthing', 'Sniffing', 'Snuffling', 'Foraging',
  'Untangling', 'Unscrambling', 'Decanting', 'Curating', 'Tinkering',
  'Whittling', 'Concocting', 'Brewing', 'Steeping', 'Distilling',
  'Fermenting', 'Crystallizing', 'Triangulating', 'Cross-checking',
  'Reticulating', 'Calibrating', 'Synthesizing', 'Decoding',
  'Disambiguating', 'Wrangling', 'Corralling', 'Herding', 'Sorting',
  'Shuffling', 'Threading', 'Stitching', 'Splicing', 'Weaving',
  'Plotting', 'Scheming', 'Conspiring', 'Divining', 'Dowsing',
  'Manifesting', 'Vibing', 'Loitering', 'Lurking', 'Skulking',
  'Spelunking', 'Excavating', 'Unearthing', 'Surfacing', 'Sussing',
  'Reckoning', 'Computing', 'Crunching', 'Chewing', 'Mulling',
  'Noodling', 'Jotting', 'Scribbling',
];

const SHARED_SENTENCES: string[] = [
  'Sniffing the terps',
  'Counting trichomes',
  'Flipping through the COA',
  'Asking the master seller',
  'Checking the cure',
  'Decoding the strain name',
  'Cross-referencing the menu',
  'Triple-checking the lab',
  "Sorting indica from sativa (don't @ us)",
  'Reading the seller\u2019s handwriting',
  'Looking for the loud',
  'Pulling the manifest',
  'Verifying the chain of custody',
  'Calling the cultivator',
  'Hunting for the gas',
  'Inspecting the nug structure',
  'Squinting at the COA fine print',
  'Matching buyer/sellers to sellers',
  'Tracking down the harvest / production date',
  'Untangling the supply chain',
  'Polishing the spec sheet',
  'Comparing pheno notes',
  'Logging the listing number',
  'Walking the drying room',
  'Checking the moisture content',
  'Rounding up the offers',
  'Confirming the pickup window',
  'Counting jars in the vault',
  'Weighing the zips',
];

// Drier subset for the admin Strategy Copilot.
const ADMIN_SENTENCES: string[] = [
  'Cross-referencing the strategy brief',
  'Reading the activation funnel',
  'Pulling the engagement deltas',
  'Triangulating the cohorts',
  'Auditing the metric definitions',
  'Mapping users to companies',
  'Walking the retention curve',
  'Inspecting the latest signals',
];

export const SENTENCES_BY_CONTEXT: Record<QuipContext, string[]> = {
  search: SHARED_SENTENCES,
  chat: SHARED_SENTENCES,
  brief: SHARED_SENTENCES,
  description: SHARED_SENTENCES,
  classifier: SHARED_SENTENCES,
  generic: [...SHARED_SENTENCES, ...ADMIN_SENTENCES],
};

// Strategy Copilot opts in via context: 'admin' aliasing - export helper.
export const ADMIN_QUIP_CONTEXT_SENTENCES = ADMIN_SENTENCES;

export const MIX_RATIO_SHORT = 0.6; // ~60% short verbs, 40% sentences

// Build an interleaved, shuffled queue of quips for one session.
// Same quip never appears twice in a row.
export function buildQuipQueue(
  context: QuipContext,
  excludeFirst?: string,
): string[] {
  const verbs = SHORT_VERBS.map((v) => `${v}\u2026`);
  const sentences = (SENTENCES_BY_CONTEXT[context] ?? SHARED_SENTENCES).map(
    (s) => `${s}\u2026`,
  );

  const shuffle = <T,>(arr: T[]): T[] => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const sv = shuffle(verbs);
  const ss = shuffle(sentences);
  const merged: string[] = [];
  let vi = 0;
  let si = 0;
  const total = sv.length + ss.length;
  for (let i = 0; i < total; i++) {
    const pickShort =
      si >= ss.length
        ? true
        : vi >= sv.length
          ? false
          : Math.random() < MIX_RATIO_SHORT;
    merged.push(pickShort ? sv[vi++] : ss[si++]);
  }

  // Avoid back-to-back repeats (rare given shuffle, but cheap to enforce).
  for (let i = 1; i < merged.length; i++) {
    if (merged[i] === merged[i - 1] && i + 1 < merged.length) {
      [merged[i], merged[i + 1]] = [merged[i + 1], merged[i]];
    }
  }

  if (excludeFirst && merged[0] === excludeFirst && merged.length > 1) {
    [merged[0], merged[1]] = [merged[1], merged[0]];
  }

  return merged;
}

// Per-quip dwell time: short words tick faster than long sentences.
export function quipDwellMs(quip: string): number {
  const words = quip.trim().split(/\s+/).length;
  if (words <= 1) return 1600;
  if (words <= 3) return 2000;
  return 2400;
}
